import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useAuth } from "../contexts/AuthContext";
import { useAudio } from "../contexts/AudioContext";
import { Flame, Trophy, Users } from "lucide-react";
import { toast } from "sonner";

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!iso) return null;
  const ms = Math.max(0, new Date(`${iso}T18:00:00+05:30`).getTime() - now);
  return { d: Math.floor(ms / 864e5), h: Math.floor(ms / 36e5) % 24, m: Math.floor(ms / 6e4) % 60, s: Math.floor(ms / 1e3) % 60 };
}

export default function Jaagran() {
  const [stats, setStats] = useState(null);
  const { user } = useAuth();
  const { playName } = useAudio();
  useSeo("Maha Shivratri Jaagran");

  const load = () => api.get("/jaagran/stats").then((r) => setStats(r.data));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const cd = useCountdown(stats?.next_shivratri);

  const chant = async () => {
    playName({ id: null, deity: "shiva", sanskrit: "ॐ नमः शिवाय", english: "Om Namah Shivaya", meaning: "Jaagran chant" });
    if (user) { toast.success("Your chant was counted. Har Har Mahadev!"); setTimeout(load, 800); }
    else toast("Sign in so your chants count on the leaderboard");
  };

  return (
    <div data-testid="jaagran-page" className="bg-[#1A0508] text-[#FAF6F0] min-h-screen -mb-24 pb-24 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        <div className="absolute right-0 top-0 font-devanagari text-[340px] leading-none text-[#D4AF37] opacity-[0.06] select-none pointer-events-none">ॐ</div>
        <div className="overline text-[#D4AF37]">॥ महाशिवरात्रि जागरण ॥</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">The Great Night of Shiva</h1>
        <p className="text-[#FAF6F0]/70 mt-5 max-w-2xl text-base md:text-lg">Stay awake with devotees around the world. Every "Om Namah Shivaya" you chant adds to the global count and to your place on the leaderboard.</p>

        <div data-testid="jaagran-countdown" className="mt-12 grid grid-cols-4 gap-3 sm:gap-5 max-w-2xl">
          {cd && [["Days", cd.d], ["Hours", cd.h], ["Minutes", cd.m], ["Seconds", cd.s]].map(([l, v]) => (
            <div key={l} className="bg-white/5 border border-[#D4AF37]/30 rounded-2xl py-5 text-center backdrop-blur">
              <div className="font-mono text-3xl sm:text-5xl text-[#F3E5AB]">{String(v).padStart(2, "0")}</div>
              <div className="overline text-[#D4AF37] mt-2 text-[0.6rem] sm:text-xs">{l}</div>
            </div>
          ))}
        </div>
        <div className="text-sm text-[#FAF6F0]/60 mt-3 font-mono" data-testid="jaagran-date">Maha Shivratri · {stats?.next_shivratri || "…"}</div>

        <div className="mt-10 flex flex-wrap gap-3">
          <button data-testid="jaagran-chant-btn" onClick={chant} className="btn-saffron text-base"><Flame className="w-4 h-4" /> Chant Om Namah Shivaya</button>
          <Link to="/deities/shiva" className="btn-ghost-gold !text-[#F3E5AB] !border-[#D4AF37]">About Lord Shiva</Link>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <Stat icon={Flame} label="Chants today" value={stats?.total_chants_today ?? 0} testid="jaagran-total" />
          <Stat icon={Users} label="Devotees chanting today" value={stats?.devotees_today ?? 0} testid="jaagran-devotees" />
          <Stat icon={Trophy} label="Top chanter" value={stats?.leaderboard?.[0]?.name || "—"} testid="jaagran-top" />
        </div>

        <h2 className="font-serif text-2xl md:text-3xl mt-16">Leaderboard · last 24 hours</h2>
        <div data-testid="jaagran-leaderboard" className="mt-6 rounded-2xl border border-[#D4AF37]/25 divide-y divide-[#D4AF37]/15 overflow-hidden">
          {(stats?.leaderboard || []).length === 0 && <div className="p-8 text-center text-[#FAF6F0]/60">No chants yet today. Be the first.</div>}
          {(stats?.leaderboard || []).map((r) => (
            <div key={r.rank} className="flex items-center gap-4 px-5 py-3 bg-white/[0.02]">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-sm ${r.rank <= 3 ? "bg-[#D4AF37] text-[#1A0508]" : "bg-white/10"}`}>{r.rank}</span>
              <span className="flex-1">{r.name}</span>
              <span className="font-mono text-[#F3E5AB]">{r.chants_24h}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white/5 border border-[#D4AF37]/25 rounded-2xl p-6">
      <Icon className="w-5 h-5 text-[#E65100]" />
      <div className="font-serif text-3xl mt-3 truncate">{value}</div>
      <div className="overline text-[#D4AF37]">{label}</div>
    </div>
  );
}
